/**
 * Whether a template has moved since a report was submitted against it.
 *
 * Separate from `template-dirty`, which asks the editor's version of the same
 * question — has this FORM diverged from the baseline it loaded. This one
 * compares two saved layouts, a live template against a report's frozen
 * snapshot, and answers for a surface the editor knows nothing about.
 */

import {deepEqual} from './deep-equal';
import {pinLogoUrls} from './logo-rehydrate';
import type {PhotoRequirement, TemplateSettings} from './types';

/** A template's drawable content: its elements and its page settings. The shape
 *  both a live template and a report's frozen snapshot arrive in. */
export interface TemplateLayout {
  requirements: readonly unknown[];
  settings: Partial<TemplateSettings>;
}

/**
 * A layout's requirements as the editor's view model — the ONE place that cast
 * is written.
 *
 * The generated wire types and `PhotoRequirement` describe the same JSON; a
 * layout carries `readonly unknown[]` because it also carries versions the app
 * has never opened. Crossing that boundary was hand-written as
 * `as unknown as PhotoRequirement[]` wherever it came up — three times in a
 * single expression in `JobReportFill` — so what is being asserted, and why it
 * holds, was restated at each of them and guaranteed by none.
 *
 * Named instead, so a reader meets the claim once and a change to the wire shape
 * has one place to break.
 */
export function layoutRequirements(requirements: readonly unknown[]): PhotoRequirement[] {
  return requirements as unknown as PhotoRequirement[];
}

/**
 * Whether the live template has moved since a report was submitted against it.
 *
 * Gates the job report's "Apply template updates" control: an amendment on a
 * report whose template nobody has touched has nothing to apply, and a button
 * that does nothing is worse than no button — it implies there is something to
 * see. A `null` snapshot means nothing has been submitted yet, which is not a
 * change.
 *
 * `pinLogoUrls` is what makes the compare meaningful. A stamped logo stores a
 * presigned URL beside its `logo_id` and that URL is re-minted on every read —
 * the snapshot holds whatever was minted at submission, the live template
 * whatever was minted last — so a raw compare reports every report as changed,
 * always. Pinning resolves each logo to its id, which is exactly the field
 * `rehydrateLogoUrls` refreshes from, so one idea of "which fields are a cache"
 * serves both.
 *
 * TODO(DERP-265 follow-up): the server holds the template row and the frozen
 * snapshot side by side and can simply say. When the API can spare a
 * `template_has_moved` (or a revision id on both), this and its plumbing delete
 * outright — the same argument `frozen_layout` itself won.
 */
export function templateHasMoved(live: TemplateLayout, frozen: TemplateLayout | null): boolean {
  if (!frozen) return false;
  return !deepEqual(comparable(live), comparable(frozen));
}

/**
 * A layout reduced to what a reader would call a change.
 *
 * The cast is the module's own: the generated wire types and this view model
 * describe the same JSON, which is why `requirements` arrives as `unknown[]`.
 *
 * Copies rather than comparing in place, deliberately. `pinLogoUrls` returns
 * every element WITHOUT a stamped logo by reference and preserves a table's
 * identity when nothing changed, so this allocates one array plus a shallow copy
 * per stamped logo — a handful, on a value memoised per template read. Skipping
 * the keys inline instead would mean a second traversal duplicating `deepEqual`,
 * which is the worse trade.
 */
function comparable(layout: TemplateLayout): {
  requirements: PhotoRequirement[];
  settings: Partial<TemplateSettings>;
} {
  return {
    requirements: pinLogoUrls(layoutRequirements(layout.requirements)),
    settings: layout.settings,
  };
}
